import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import IconButton from "@mui/material/IconButton";
import clsx from "clsx";
import { JSX, useEffect, useState } from "react";

import { HeaderCandidate } from "../Header/HeaderCandidate";
import {
  ICandidate,
  ScoreDetailItem,
  ScoreDetailSection,
} from "@src/shared/types";

import markedImg from "@src/shared/assets/marked_img.svg";
import failedImg from "@src/shared/assets/failed_img.svg";
import heartImg from "@src/shared/assets/heart.svg";
import shieldImg from "@src/shared/assets/shield.svg";
import starImg from "@src/shared/assets/star.svg";
import warningImg from "@src/shared/assets/warning_img.svg";

import styles from "./ScoreDetailsOverlay.module.scss";

type ScoreDetailsOverlayProps = {
  isOpen: boolean;
  onClose: () => void;
  totalScore?: number | null;
  sections: ScoreDetailSection[];
  searchCandidateResult: ICandidate;
  searchRequestResult: ICandidate[];
};

type ScoreStatus = "success" | "warning" | "danger" | "empty";
type MatchingStatus = "high" | "partial" | "low";

const SCORE_STATUS_ORDER: Record<ScoreStatus, number> = {
  success: 1,
  warning: 2,
  danger: 3,
  empty: 4,
};

const statusDictionary: Record<string, JSX.Element> = {
  "Обязательные требования": <img src={shieldImg} />,
  "Приоритетные требования": <img src={heartImg} />,
  "Опциональные требования": <img src={starImg} />,
};

const ANIMATION_DURATION = 230;

const getScoreStatus = (
  score: number | null | undefined,
  maxScore: number | null | undefined,
): ScoreStatus => {
  if (
    score === null ||
    score === undefined ||
    maxScore === null ||
    maxScore === undefined ||
    maxScore === 0
  ) {
    return "empty";
  }

  if (score === 0) {
    return "danger";
  }

  if (score < maxScore) {
    return "warning";
  }

  return "success";
};

const sortItemsByScoreStatus = (items: ScoreDetailItem[]) => {
  return [...items].sort((a, b) => {
    const statusA = getScoreStatus(a.score, a.maxScore);
    const statusB = getScoreStatus(b.score, b.maxScore);

    const statusDiff =
      SCORE_STATUS_ORDER[statusA] - SCORE_STATUS_ORDER[statusB];

    if (statusDiff !== 0) {
      return statusDiff;
    }

    const scoreA = a.score ?? 0;
    const scoreB = b.score ?? 0;

    return scoreB - scoreA;
  });
};

const renderStatusIcon = (status: ScoreStatus) => {
  if (status === "success") {
    return <img src={markedImg} />;
  }

  if (status === "warning") {
    return <img src={warningImg} />;
  }

  if (status === "danger") {
    return <img src={failedImg} />;
  }

  return <WarningAmberIcon fontSize="small" sx={{ color: "#A4BBC7" }} />;
};

const getMatchingStatus = (
  score: number | null | undefined,
): MatchingStatus => {
  if (score === null || score === undefined) {
    return "low";
  }

  if (score >= 90) {
    return "high";
  }

  if (score >= 80) {
    return "partial";
  }

  return "low";
};

const getMatchingText = (status: MatchingStatus) => {
  if (status === "high") {
    return "Высокое соответствие";
  }

  if (status === "partial") {
    return "Частичное соответствие";
  }

  return "Низкое соответствие";
};

const renderMatchingIcon = (status: MatchingStatus) => {
  if (status === "high") {
    return (
      <VerifiedOutlinedIcon
        fontSize="medium"
        sx={{ color: "#00C195", width: "30px", height: "30px" }}
      />
    );
  }

  if (status === "partial") {
    return (
      <ReportProblemOutlinedIcon
        fontSize="medium"
        sx={{ color: "#F2C94C", width: "30px", height: "30px" }}
      />
    );
  }

  return (
    <HighlightOffOutlinedIcon
      fontSize="medium"
      sx={{ color: "#FF0002", width: "30px", height: "30px" }}
    />
  );
};

const getScoreWord = (score: number | null | undefined) => {
  if (score === null || score === undefined) {
    return "баллов";
  }

  const lastDigit = score % 10;
  const lastTwoDigits = score % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return "баллов";
  }

  if (lastDigit === 1) {
    return "балл";
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return "балла";
  }

  return "баллов";
};

const formatScore = (
  score: number | null | undefined,
  maxScore: number | null | undefined,
) => {
  if (
    score === null ||
    score === undefined ||
    maxScore === null ||
    maxScore === undefined
  ) {
    return "Нет данных";
  }

  return `${score} из ${maxScore}`;
};

export const ScoreDetailsOverlay = ({
  isOpen,
  onClose,
  totalScore,
  sections,
  searchCandidateResult,
  searchRequestResult,
}: ScoreDetailsOverlayProps) => {
  const [isMounted, setIsMounted] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen && isMounted) {
      const timeoutId = window.setTimeout(() => {
        setIsMounted(false);
      }, ANIMATION_DURATION);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }
  }, [isOpen, isMounted]);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMounted]);

  if (!isMounted) {
    return null;
  }

  const visibleSections = sections
    .filter((section) => section.items?.length)
    .map((section) => ({
      ...section,
      items: sortItemsByScoreStatus(section.items),
    }));

  const matchingStatus = getMatchingStatus(totalScore);
  const matchingText = getMatchingText(matchingStatus);

  return (
    <div
      className={clsx(styles.overlay, {
        [styles.overlayOpen]: isOpen,
        [styles.overlayClose]: !isOpen,
      })}
    >
      <div className={styles.backdrop} />

      <aside className={styles.panel}>
        <div className={styles.panelInner}>
          <div className={styles.panelHeader}>
            <div>
              <HeaderCandidate
                searchCandidateResult={searchCandidateResult}
                searchRequestResult={searchRequestResult}
                isOverlay={isOpen}
              />
            </div>

            <IconButton onClick={onClose} className={styles.closeButton}>
              <CloseIcon className={styles.closeButtonImg} />
            </IconButton>
          </div>

          <div className={styles.sections}>
            <section className={clsx(styles.section, styles.sectionMatching)}>
              <div className={styles.imgWrapper}>
                {renderMatchingIcon(matchingStatus)}
              </div>

              <div
                className={clsx(
                  styles.infoMatching,
                  styles[`infoMatching_${matchingStatus}`],
                )}
              >
                <span>
                  {matchingText} — {totalScore ?? "Нет данных"}{" "}
                  {getScoreWord(totalScore)} из 100
                </span>
              </div>
            </section>

            {!visibleSections.length ? (
              <div className={styles.empty}>Данных нет</div>
            ) : (
              visibleSections.map((section, sectionIndex) => (
                <section
                  className={styles.section}
                  key={section.id || `${section.title}-${sectionIndex}`}
                >
                  <div className={styles.sectionHeader}>
                    <div className={styles.sectionImg}>
                      {statusDictionary[section.title]}
                    </div>

                    <div className={styles.sectionTitle}>{section.title}:</div>

                    <div className={styles.sectionScore}>
                      {formatScore(section.score, section.maxScore)}
                    </div>
                  </div>

                  <div className={styles.items}>
                    {section.items.map((item, itemIndex) => {
                      const status = getScoreStatus(item.score, item.maxScore);

                      return (
                        <div
                          className={styles.item}
                          key={item.id || `${item.title}-${itemIndex}`}
                        >
                          <div
                            className={clsx(
                              styles.itemIcon,
                              styles[`itemIcon_${status}`],
                            )}
                          >
                            {renderStatusIcon(status)}
                          </div>

                          <div className={styles.itemTitle}>{item.title}</div>

                          <div className={styles.itemScore}>
                            {formatScore(item.score, item.maxScore)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))
            )}
          </div>
        </div>
      </aside>
    </div>
  );
};
